// src/components/pos/ui/Input.jsx
// POS-specific input components for touch interfaces

import { forwardRef, useState } from 'react'
import { cn } from '../../../utils/cn'
import { Search, X, Delete } from 'lucide-react'

// Base POS Input - Large touch-friendly input
const POSInput = forwardRef(({ 
  className, 
  type = "text",
  size = "default",
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  ...props 
}, ref) => {
  const sizes = {
    sm: "h-10 px-3 text-sm",
    default: "h-12 px-4 text-base", 
    lg: "h-14 px-5 text-lg"
  }

  return (
    <div className="relative">
      {LeftIcon && (
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <LeftIcon className="h-5 w-5" />
        </div>
      )}
      <input
        type={type}
        className={cn(
          "w-full border border-gray-300 rounded-lg bg-white text-gray-900",
          "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
          "disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
          "touch-manipulation", // Improves touch response
          LeftIcon && "pl-10",
          RightIcon && "pr-10",
          sizes[size],
          className
        )}
        ref={ref}
        {...props}
      />
      {RightIcon && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <RightIcon className="h-5 w-5" />
        </div>
      )}
    </div>
  )
})

// Search Input for products
const SearchInput = forwardRef(({ 
  value,
  onChange,
  onClear,
  placeholder = "Search products...",
  className,
  ...props 
}, ref) => {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
        <Search className="h-5 w-5" />
      </div>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "w-full h-12 pl-10 pr-10 border border-gray-300 rounded-lg bg-white text-gray-900",
          "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
          "touch-manipulation",
          className
        )}
        {...props}
      />
      {value && (
        <button
          onClick={onClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-manipulation"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  )
})

// Number Pad Component
const NumberPad = forwardRef(({ 
  onNumber,
  onClear,
  onDelete,
  onEnter,
  className,
  ...props 
}, ref) => {
  const buttons = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: 'clear', label: 'Clear', action: onClear },
    { value: '0', label: '0' },
    { value: 'delete', label: '⌫', action: onDelete },
  ]

  const handleButtonClick = (button) => {
    if (button.action) {
      button.action()
    } else if (button.value !== 'clear' && button.value !== 'delete') {
      onNumber?.(button.value)
    }
  }

  return (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-3 gap-3 p-4 bg-white border border-gray-200 rounded-lg",
        className
      )}
      {...props}
    >
      {buttons.map((button) => (
        <button
          key={button.value}
          onClick={() => handleButtonClick(button)}
          className={cn(
            "h-12 rounded-lg font-medium text-lg transition-all duration-200",
            "focus:outline-none focus:ring-2 focus:ring-orange-500/50",
            "touch-manipulation active:scale-95",
            button.value === 'clear' || button.value === 'delete'
              ? "bg-gray-500 text-white hover:bg-gray-600"
              : "bg-gray-100 text-gray-900 hover:bg-gray-200"
          )}
        >
          {button.label}
        </button>
      ))}
      
      {onEnter && (
        <button
          onClick={onEnter}
          className="col-span-3 h-12 bg-orange-500 text-white rounded-lg font-medium text-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 touch-manipulation active:scale-95 mt-3"
        >
          Enter
        </button>
      )}
    </div>
  )
})

// Quantity Input with +/- buttons
const QuantityInput = forwardRef(({ 
  value = 0,
  onChange,
  min = 0,
  max = 999,
  className,
  ...props 
}, ref) => {
  const handleDecrease = () => {
    const newValue = Math.max(min, value - 1)
    onChange?.(newValue)
  }

  const handleIncrease = () => {
    const newValue = Math.min(max, value + 1)
    onChange?.(newValue)
  }

  const handleInputChange = (e) => {
    const newValue = parseInt(e.target.value) || 0
    if (newValue >= min && newValue <= max) {
      onChange?.(newValue)
    }
  }

  return (
    <div className={cn("flex items-center space-x-2", className)} {...props}>
      <button
        onClick={handleDecrease}
        disabled={value <= min}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg font-medium touch-manipulation"
      >
        -
      </button>
      <input
        ref={ref}
        type="number"
        value={value}
        onChange={handleInputChange}
        min={min}
        max={max}
        className="w-16 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 touch-manipulation"
      />
      <button
        onClick={handleIncrease}
        disabled={value >= max}
        className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg font-medium touch-manipulation"
      >
        +
      </button>
    </div>
  )
})

// Amount Input with currency formatting
const AmountInput = forwardRef(({ 
  value,
  onChange,
  currency = "Rs.",
  placeholder = "0.00",
  className,
  ...props 
}, ref) => {
  const handleChange = (e) => {
    const inputValue = e.target.value
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(inputValue)) {
      onChange?.(inputValue)
    }
  }

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
        {currency}
      </div>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn(
          "w-full h-12 pl-12 pr-4 border border-gray-300 rounded-lg bg-white text-gray-900",
          "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
          "text-right font-medium touch-manipulation",
          className
        )}
        {...props}
      />
    </div>
  )
})

// Phone Input with country code
const PhoneInput = forwardRef(({ 
  value,
  onChange,
  countryCode = "+94",
  placeholder = "771234567",
  className,
  ...props 
}, ref) => {
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">
        {countryCode}
      </div>
      <input
        ref={ref}
        type="tel"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "w-full h-12 pl-16 pr-4 border border-gray-300 rounded-lg bg-white text-gray-900",
          "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
          "touch-manipulation",
          className
        )}
        {...props}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-500 hover:text-orange-700 touch-manipulation"
        >
          <Search className="h-5 w-5" />
        </button>
      )}
    </div>
  )
})

// Date Input for orders
const DateInput = forwardRef(({ 
  value,
  onChange,
  label,
  className,
  ...props 
}, ref) => {
  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={onChange}
        className="w-full h-12 px-4 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 touch-manipulation"
        {...props}
      />
    </div>
  )
})

// Time Input for orders
const TimeInput = forwardRef(({ 
  value,
  onChange,
  label,
  className,
  ...props 
}, ref) => {
  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <input
        ref={ref}
        type="time"
        value={value}
        onChange={onChange}
        className="w-full h-12 px-4 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 touch-manipulation"
        {...props}
      />
    </div>
  )
})

// Notes/Comments textarea
const NotesInput = forwardRef(({ 
  value,
  onChange,
  placeholder = "Add special instructions...",
  maxLength = 200,
  className,
  ...props 
}, ref) => {
  return (
    <div className="space-y-2">
      <textarea
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={3}
        className={cn(
          "w-full p-4 border border-gray-300 rounded-lg bg-white text-gray-900 resize-none",
          "focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500",
          "touch-manipulation",
          className
        )}
        {...props}
      />
      {maxLength && (
        <div className="text-right text-xs text-gray-500">
          {value?.length || 0}/{maxLength}
        </div>
      )}
    </div>
  )
})

POSInput.displayName = "POSInput"
SearchInput.displayName = "SearchInput"
NumberPad.displayName = "NumberPad"
QuantityInput.displayName = "QuantityInput"
AmountInput.displayName = "AmountInput"
PhoneInput.displayName = "PhoneInput"
DateInput.displayName = "DateInput"
TimeInput.displayName = "TimeInput"
NotesInput.displayName = "NotesInput"

export {
  POSInput,
  SearchInput,
  NumberPad,
  QuantityInput,
  AmountInput,
  PhoneInput,
  DateInput,
  TimeInput,
  NotesInput
}